import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, sendAnalyticsBeacon } from '../utils/api';
import { useTranslation } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { CheckCircle, XCircle, ChevronRight, ChevronLeft, Play } from 'lucide-react';
import { toast } from 'sonner';
import StartScreen from '../components/StartScreen';
import CompletionScreen from '../components/CompletionScreen';
import ClosedScreen from '../components/ClosedScreen';
import UnavailableScreen from '../components/UnavailableScreen';
import ReelContainer from '../components/ReelContainer';
import DOMPurify from 'dompurify';
import { useViewportHeight, useLockBodyScroll } from '../hooks/useViewportHeight';

export default function Player() {
  const { t, tm } = useTranslation();
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  
  const [course, setCourse] = useState(null);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizComments, setQuizComments] = useState({});
  // Per-run session ID for mystery_shop isolation (generated at start)
  const [sessionId, setSessionId] = useState(null);
  const [showQuizResult, setShowQuizResult] = useState(false);
  // Quiz locking: store submitted answers per block to prevent re-submission
  // Structure: { [blockId]: { answers: {}, isLocked: true, isCorrect: boolean|null, selectedChoices: [] } }
  const [lockedQuizzes, setLockedQuizzes] = useState({});
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [unavailableMessage, setUnavailableMessage] = useState('');
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  
  // Time-gating state for non-media blocks
  const [canProceed, setCanProceed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const timerRef = useRef(null);
  
  // ==================== SIMPLIFIED VIDEO STATE MODEL ====================
  // For video blocks: simple play/pause/ended tracking
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasWatchedToEnd, setHasWatchedToEnd] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  // Duration and countdown
  const [durationSec, setDurationSec] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  // CRITICAL: Only process media events after user has explicitly tapped play
  const [hasUserPressedPlay, setHasUserPressedPlay] = useState(false);
  // Instruction hint visibility (show only first 2 seconds)
  const [showInstructionHint, setShowInstructionHint] = useState(true);
  const instructionHintTimerRef = useRef(null);
  
  // User data & screen states
  const [userData, setUserData] = useState(null);
  const [showStartScreen, setShowStartScreen] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [showClosedScreen, setShowClosedScreen] = useState(false);
  
  const [visitorId] = useState(() => {
    let id = localStorage.getItem('qoldo_visitor_id');
    if (!id) {
      id = 'v_' + Math.random().toString(36).substring(2);
      localStorage.setItem('qoldo_visitor_id', id);
    }
    return id;
  });

  // iOS Safari viewport height fix - sets --vh CSS variable
  useViewportHeight();
  // Lock body scroll to prevent iOS bounce/overscroll
  useLockBodyScroll(true);

  // === Portrait-only guard =================================================
  // Mobile/tablet Safari & Chrome cannot be prevented from rotating the
  // viewport. When that happens on a touch device, show a blocking overlay
  // and pause any playing media. Auto-clears when the user rotates back.
  // Desktop / laptop / mouse-pointer users are NEVER affected.
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    // Touch device gate — avoids desktop false positives.
    // (hover: none) and (pointer: coarse) → phones + tablets in touch mode.
    // Short-side ≤ 900px → excludes iPads used as laptops / large touch monitors.
    const touchMql = window.matchMedia('(hover: none) and (pointer: coarse)');
    const shortSide = Math.min(window.innerWidth || 0, window.innerHeight || 0);
    const isTouchDevice = touchMql.matches && shortSide <= 900;
    if (!isTouchDevice) {
      setIsLandscape(false);
      return;
    }

    const landscapeMql = window.matchMedia('(orientation: landscape)');
    const update = () => setIsLandscape(landscapeMql.matches);
    update();
    // Modern + legacy Safari listeners
    if (landscapeMql.addEventListener) {
      landscapeMql.addEventListener('change', update);
    } else if (landscapeMql.addListener) {
      landscapeMql.addListener(update);
    }
    return () => {
      if (landscapeMql.removeEventListener) {
        landscapeMql.removeEventListener('change', update);
      } else if (landscapeMql.removeListener) {
        landscapeMql.removeListener(update);
      }
    };
  }, []);

  // Auto-pause any playing media when we enter landscape.
  useEffect(() => {
    if (!isLandscape) return;
    try { videoRef.current && videoRef.current.pause && videoRef.current.pause(); } catch (_) { /* ignore */ }
    try { audioRef.current && audioRef.current.pause && audioRef.current.pause(); } catch (_) { /* ignore */ }
  }, [isLandscape]);

  // CRITICAL: Force video to stay paused until user explicitly presses play
  // This catches any unwanted browser autoplay behavior
  useEffect(() => {
    const video = videoRef.current;
    const block = course?.blocks?.[currentBlockIndex];
    
    if (!video || block?.type !== 'video') return;
    
    // If user hasn't pressed play, force video to stay paused
    if (!hasUserPressedPlay) {
      const checkAndPause = () => {
        if (!video) return;
        
        // Force pause if video is playing OR if currentTime > 0
        const isUnwantedPlayback = !video.paused || video.currentTime > 0.1;
        
        if (isUnwantedPlayback && !hasUserPressedPlay) {
          console.log('[Player] FORCED PAUSE: Video tried to autoplay without user action, currentTime:', video.currentTime);
          video.pause();
          video.currentTime = 0;
        }
      };
      
      // Check immediately
      checkAndPause();
      
      // Check very frequently (every 50ms)
      const intervalId = setInterval(checkAndPause, 50);
      
      // Also add event listeners as backup
      const handleUnwantedPlay = () => {
        if (!hasUserPressedPlay) {
          console.log('[Player] BLOCKED: unwanted play event');
          video.pause();
          video.currentTime = 0;
        }
      };
      
      video.addEventListener('play', handleUnwantedPlay);
      video.addEventListener('playing', handleUnwantedPlay);
      
      return () => {
        clearInterval(intervalId);
        video.removeEventListener('play', handleUnwantedPlay);
        video.removeEventListener('playing', handleUnwantedPlay);
      };
    } else {
      // User HAS pressed play - ensure video actually plays
      // This handles iOS where autoPlay might not work reliably
      const ensureVideoPlays = () => {
        if (video && video.paused && hasUserPressedPlay) {
          console.log('[Player] Ensuring video plays after user tap');
          video.play().then(() => {
            console.log('[Player] Video play() successful');
            setIsPlaying(true);
          }).catch((err) => {
            console.log('[Player] Video play() failed:', err.message);
            // If play fails, show the resume overlay
            setIsPlaying(false);
          });
        }
      };
      
      // Try to play after a short delay to ensure DOM is ready
      const timeoutId = setTimeout(ensureVideoPlays, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [course, currentBlockIndex, hasUserPressedPlay]);

  // === Portrait-video safeguard ============================================
  // Attach the vendor-prefixed attributes that React 19 rejects on JSX
  // (webkit-playsinline, x5-video-player-*) and bind the iOS-specific
  // webkitbeginfullscreen event listener — which exits fullscreen the moment
  // it is triggered. This prevents native landscape/fullscreen takeover on
  // iOS Safari, Android Chrome, and WeChat/QQ in-app browsers.
  useEffect(() => {
    const video = videoRef.current;
    const block = course?.blocks?.[currentBlockIndex];
    if (!video || block?.type !== 'video' || !hasUserPressedPlay) return;

    try {
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('x5-video-player-type', 'h5');
      video.setAttribute('x5-video-player-fullscreen', 'false');
    } catch (_) { /* attribute set failure is safe to ignore */ }

    const exitFs = () => {
      try {
        if (typeof video.webkitExitFullscreen === 'function') {
          video.webkitExitFullscreen();
        }
        if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
          document.exitFullscreen();
        }
      } catch (_) { /* browser rejected exit — safe to ignore */ }
    };
    video.addEventListener('webkitbeginfullscreen', exitFs);
    video.addEventListener('fullscreenchange', exitFs);
    document.addEventListener('fullscreenchange', exitFs);

    return () => {
      video.removeEventListener('webkitbeginfullscreen', exitFs);
      video.removeEventListener('fullscreenchange', exitFs);
      document.removeEventListener('fullscreenchange', exitFs);
    };
  }, [course, currentBlockIndex, hasUserPressedPlay]);

  // Fetch course data
  useEffect(() => {
    fetchCourse();
    trackEvent('view');
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const url = `/player/${courseId}${token ? `?t=${token}` : ''}`;
      const response = await api.get(url);
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      if (error.response?.status === 404 || error.response?.status === 410) {
        setUnavailable(true);
        setUnavailableMessage(error.response?.data?.detail || 'Deze training is niet langer beschikbaar.');
      } else {
        toast.error('Failed to load course');
      }
    } finally {
      setLoading(false);
    }
  };

  const trackEvent = useCallback((eventType, meta = {}) => {
    sendAnalyticsBeacon({
      course_id: courseId,
      visitor_id: visitorId,
      event_type: eventType,
      meta: {
        ...meta,
        client_id: course?.client_id || '',
        store_id: userData?.store_id || '',
        store_name: userData?.store_name || '',
        user_name: userData?.user_name || '',
        first_name: userData?.first_name || '',
        last_name: userData?.last_name || '',
        ...(sessionId ? { session_id: sessionId } : {})
      }
    });
  }, [courseId, visitorId, userData, course?.client_id, sessionId]);

  // Initialize gating for current block
  const initializeBlockGating = useCallback((block) => {
    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Clear instruction hint timer
    if (instructionHintTimerRef.current) {
      clearTimeout(instructionHintTimerRef.current);
      instructionHintTimerRef.current = null;
    }
    
    // Reset all gating states
    setCanProceed(false);
    setTimeRemaining(0);
    
    // Reset video state to clean slate - CRITICAL for preventing autoplay
    setIsPlaying(false);
    setHasWatchedToEnd(false);
    setIsOverlayVisible(true);
    setDurationSec(null);
    setRemainingSeconds(null);
    setHasUserPressedPlay(false);  // CRITICAL: Reset user-initiated flag
    setShowInstructionHint(true);  // Reset instruction hint for new block
    
    // HARD RESET video element to prevent browser auto-resume
    if (block?.type === 'video' && videoRef.current) {
      const video = videoRef.current;
      try {
        video.pause();
      } catch (e) {
        // ignore
      }
      video.currentTime = 0;
      console.log('[Player] Video hard reset: paused at time 0');
    }
    
    // HARD RESET audio element
    if (block?.type === 'audio' && audioRef.current) {
      const audio = audioRef.current;
      try {
        audio.pause();
      } catch (e) {
        // ignore
      }
      audio.currentTime = 0;
      console.log('[Player] Audio hard reset: paused at time 0');
    }
    
    if (!block) return;
    
    console.log('[Player] Initializing gating for block:', block.type, block.id);
    
    // VIDEO/AUDIO: gated until watched to end
    // canProceed stays false until onEnded fires
    if (block.type === 'video' || block.type === 'audio') {
      console.log('[Player] Media block - gated until watched to end');
      // canProceed stays false - will be set true by onEnded
      return;
    }
    
    // Quiz blocks: can proceed after submission
    if (block.type === 'quiz') {
      return;
    }
    
    // Other blocks: check min_view_seconds
    const minViewSeconds = block.props?.min_view_seconds || 0;
    
    if (minViewSeconds > 0) {
      setTimeRemaining(minViewSeconds);
      
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setCanProceed(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCanProceed(true);
    }
  }, []);

  // Handle "Start Course" button - ALWAYS show StartScreen
  const handleStart = () => {
    setShowStartScreen(true);
  };

  // Handle StartScreen submission
  const handleStartScreenSubmit = (data) => {
    // Generate per-run session_id for mystery_shop isolation
    const runSessionId = course?.experience_type === 'mystery_shop'
      ? 'ms_' + Math.random().toString(36).substring(2) + Date.now().toString(36)
      : null;
    setSessionId(runSessionId);
    setUserData(data);
    setShowStartScreen(false);
    setStarted(true);
    trackEvent('start', { 
      store_id: data.store_id,
      store_name: data.store_name,
      user_name: data.user_name,
      first_name: data.first_name,
      last_name: data.last_name,
      ...(runSessionId ? { session_id: runSessionId } : {})
    });
    if (course?.blocks?.[0]) {
      trackEvent('block_view', { block_id: course.blocks[0].id });
      initializeBlockGating(course.blocks[0]);
    }
  };
  
  // ==================== SIMPLIFIED VIDEO EVENT HANDLERS ====================
  // CRITICAL: All handlers check hasUserPressedPlay to ignore browser auto-events
  
  // Called when media metadata is loaded - we get duration here
  // This can run before user presses play, that's OK - we just get the duration
  const handleMediaLoadedMetadata = (e) => {
    const video = e.target;
    if (!video) return;
    
    const d = video.duration;
    console.log('[Player] onLoadedMetadata:', { duration: d, hasUserPressedPlay });
    
    if (Number.isFinite(d) && d > 0) {
      const rounded = Math.ceil(d);
      setDurationSec(rounded);
      setRemainingSeconds(rounded);
    } else {
      setDurationSec(null);
      setRemainingSeconds(null);
    }
  };
  
  // Called when video starts playing
  const handleMediaPlay = (e) => {
    console.log('[Player] onPlay fired, hasUserPressedPlay:', hasUserPressedPlay);
    
    // CRITICAL: Ignore if user hasn't explicitly pressed play
    if (!hasUserPressedPlay) {
      console.log('[Player] Ignoring onPlay - user has not pressed play yet');
      // Force pause the video - this is an unwanted autoplay
      try {
        e.target?.pause();
      } catch (err) {
        // ignore
      }
      return;
    }
    
    setIsPlaying(true);
    setIsOverlayVisible(false);
    
    // Start 2-second timer to hide instruction hint
    // Clear any existing timer first
    if (instructionHintTimerRef.current) {
      clearTimeout(instructionHintTimerRef.current);
    }
    setShowInstructionHint(true); // Show hint when play starts
    instructionHintTimerRef.current = setTimeout(() => {
      setShowInstructionHint(false);
    }, 2000);
  };
  
  // Called when playback actually starts (more reliable on iOS)
  const handleMediaPlaying = (e) => {
    console.log('[Player] onPlaying fired, hasUserPressedPlay:', hasUserPressedPlay);
    
    // CRITICAL: Ignore if user hasn't explicitly pressed play
    if (!hasUserPressedPlay) {
      console.log('[Player] Ignoring onPlaying - user has not pressed play yet');
      // Force pause the video - this is an unwanted autoplay
      try {
        e.target?.pause();
      } catch (err) {
        // ignore
      }
      return;
    }
    
    setIsPlaying(true);
    setIsOverlayVisible(false);
    
    // Try to get duration if we don't have it yet
    const video = e.target;
    if (durationSec === null && video) {
      const d = video.duration;
      if (Number.isFinite(d) && d > 0) {
        const rounded = Math.ceil(d);
        setDurationSec(rounded);
        setRemainingSeconds(Math.ceil(rounded - video.currentTime));
      }
    }
  };
  
  // Called when video is paused
  const handleMediaPause = (e) => {
    const video = e.target;
    console.log('[Player] onPause fired', { currentTime: video?.currentTime, hasUserPressedPlay });
    
    // If user hasn't pressed play, ignore pause events
    if (!hasUserPressedPlay) {
      console.log('[Player] Ignoring onPause - user has not pressed play yet');
      return;
    }
    
    // Check if we're at the end (iOS fires pause before ended)
    if (video && durationSec && Number.isFinite(video.currentTime)) {
      const isAtEnd = Math.abs(video.currentTime - durationSec) < 0.5;
      if (isAtEnd && !hasWatchedToEnd) {
        console.log('[Player] Pause at end - treating as ended');
        setHasWatchedToEnd(true);
        setCanProceed(true);
        setIsPlaying(false);
        setIsOverlayVisible(false);
        setRemainingSeconds(0);
        return;
      }
    }
    
    // Normal pause (before end)
    setIsPlaying(false);
    if (!hasWatchedToEnd) {
      setIsOverlayVisible(true);
    }
  };
  
  // Called periodically during playback
  const handleMediaTimeUpdate = (e) => {
    // CRITICAL: Ignore if user hasn't pressed play
    if (!hasUserPressedPlay) return;
    
    const video = e.target;
    if (!video || hasWatchedToEnd) return;
    
    const currentTime = video.currentTime;
    if (!Number.isFinite(currentTime)) return;
    
    // Try to get duration if we don't have it
    let currentDuration = durationSec;
    if (currentDuration === null) {
      const d = video.duration;
      if (Number.isFinite(d) && d > 0) {
        currentDuration = Math.ceil(d);
        setDurationSec(currentDuration);
      }
    }
    
    // Update remaining seconds for countdown
    if (currentDuration !== null) {
      const remaining = Math.max(0, Math.ceil(currentDuration - currentTime));
      setRemainingSeconds(remaining);
      
      // Detect end via timeupdate (iOS workaround)
      if (remaining <= 0 && !hasWatchedToEnd) {
        console.log('[Player] Video ended via timeupdate');
        setHasWatchedToEnd(true);
        setCanProceed(true);
        setIsPlaying(false);
        setIsOverlayVisible(false);
        setRemainingSeconds(0);
      }
    }
  };
  
  // Called when video reaches the end
  const handleMediaEnded = (e) => {
    console.log('[Player] onEnded fired, hasUserPressedPlay:', hasUserPressedPlay);
    
    // CRITICAL: Ignore if user hasn't pressed play
    if (!hasUserPressedPlay) {
      console.log('[Player] Ignoring onEnded - user has not pressed play yet');
      return;
    }
    
    setHasWatchedToEnd(true);
    setCanProceed(true);
    setIsPlaying(false);
    setIsOverlayVisible(false);
    setRemainingSeconds(0);
  };
  
  // Called when media fails to load
  const handleMediaError = (e) => {
    const video = e.target;
    const error = video?.error;
    console.log('[Player] onError:', { code: error?.code, message: error?.message });
    
    // On error: durationSec stays null, so canGoToNext() will allow proceed
    // User can click Next to continue - they won't be blocked
    // Keep overlay visible so they can try to play or click Next
  };
  
  // Handle play overlay click - sets flag to render video element AND sets playing state
  const handlePlayClick = () => {
    console.log('[Player] User tapped play overlay - will render video element');
    setHasUserPressedPlay(true);
    // CRITICAL FIX: Set isPlaying to true immediately to hide overlay
    // The video element will render with autoPlay
    // If autoPlay fails, onPause will fire and set isPlaying back to false
    setIsPlaying(true);
    setIsOverlayVisible(false);
  };

  // Navigate to previous block
  const handlePrev = () => {
    if (currentBlockIndex > 0) {
      const prevIndex = currentBlockIndex - 1;
      const prevBlock = course.blocks[prevIndex];
      setCurrentBlockIndex(prevIndex);
      
      // For locked quizzes: restore saved answers and show result state
      if (prevBlock?.type === 'quiz' && lockedQuizzes[prevBlock.id]?.isLocked) {
        setQuizAnswers(lockedQuizzes[prevBlock.id].answers || {});
        setShowQuizResult(true);
      } else {
        setShowQuizResult(false);
        setQuizAnswers({});
      }
      
      trackEvent('block_view', { block_id: prevBlock?.id });
      initializeBlockGating(prevBlock);
    }
  };

  // Check if user can proceed to next block
  // Helper: check if must_finish is enabled (handles true, "true", 1)
  const isMustFinishEnabled = (val) => {
    const result = val === true || val === 'true' || val === 1;
    return result;
  };

  const canGoToNext = () => {
    const block = course?.blocks?.[currentBlockIndex];
    if (!block) return false;
    
    // Quiz: must have submitted answer OR be locked (already answered)
    if (block.type === 'quiz') {
      // If quiz is locked, user can always proceed (they already answered)
      if (lockedQuizzes[block.id]?.isLocked) return true;
      return showQuizResult;
    }
    
    // Video: Check must_finish property (default: false = skippable)
    if (block.type === 'video') {
      const mustFinishVideo = isMustFinishEnabled(block.props?.must_finish);
      // SKIPPABLE: If must_finish is NOT enabled, allow immediate proceed
      if (!mustFinishVideo) return true;
      // MUST WATCH: must_finish is enabled - MUST watch to end
      // Only allow proceed if hasWatchedToEnd is true
      return hasWatchedToEnd;
    }
    
    // Audio: always must watch to end (existing behavior)
    if (block.type === 'audio') {
      return hasWatchedToEnd;
    }
    
    // Other blocks: use canProceed (based on min_view_seconds timer)
    return canProceed;
  };

  // Navigate to next block or show completion screen
  const handleNext = () => {
    if (!canGoToNext()) {
      const block = course.blocks[currentBlockIndex];
      
      if (block.type === 'video' || block.type === 'audio') {
        // Show remaining time message
        if (remainingSeconds !== null && remainingSeconds > 0) {
          toast.error(t('player_watch_more', { seconds: remainingSeconds }));
        } else {
          toast.error(t('player_watch_to_end'));
        }
      } else if (timeRemaining > 0) {
        toast.error(t('player_wait_more', { seconds: timeRemaining }));
      } else {
        toast.error(t('player_complete_first'));
      }
      return;
    }
    
    const nextIndex = currentBlockIndex + 1;
    
    if (nextIndex >= course.blocks.length) {
      trackEvent('complete');
      setShowCompletionScreen(true);
      return;
    }
    
    const nextBlock = course.blocks[nextIndex];
    setCurrentBlockIndex(nextIndex);
    
    // For locked quizzes: restore saved state when navigating forward too
    if (nextBlock?.type === 'quiz' && lockedQuizzes[nextBlock.id]?.isLocked) {
      setQuizAnswers(lockedQuizzes[nextBlock.id].answers || {});
      setShowQuizResult(true);
    } else {
      setShowQuizResult(false);
      setQuizAnswers({});
    }
    
    trackEvent('block_view', { block_id: nextBlock?.id });
    initializeBlockGating(nextBlock);
  };

  // Handle quiz submission
  const handleQuizSubmit = () => {
    const block = course.blocks[currentBlockIndex];
    
    // If this quiz is already locked, don't process again (just show result)
    if (lockedQuizzes[block.id]?.isLocked) {
      setShowQuizResult(true);
      return;
    }
    
    const selectedChoices = Object.keys(quizAnswers).filter(id => quizAnswers[id]);
    
    if (selectedChoices.length === 0) {
      toast.error(t('quiz_select_answer'));
      return;
    }
    
    const correctChoices = block.props.choices.filter(c => c.correct).map(c => c.id);
    const hasCorrectAnswer = correctChoices.length > 0;
    
    let isCorrect = null;
    if (hasCorrectAnswer) {
      isCorrect = selectedChoices.length === correctChoices.length &&
        selectedChoices.every(id => correctChoices.includes(id));
    }
    
    setShowQuizResult(true);
    
    // Lock this quiz: save ALL state needed to restore the quiz view
    setLockedQuizzes(prev => ({
      ...prev,
      [block.id]: {
        answers: { ...quizAnswers },
        isLocked: true,
        isCorrect: isCorrect,
        selectedChoices: selectedChoices,
        hasCorrectAnswer: hasCorrectAnswer
      }
    }));
    
    // Send analytics event (only once, since we check isLocked above)
    trackEvent('quiz_submit', {
      block_id: block.id,
      question: block.props?.question || '',
      correct: isCorrect,
      selected: selectedChoices,
      has_correct_answer: hasCorrectAnswer
    });
  };

  // Handle completion -> closed screen
  const handleFinish = () => {
    setShowCompletionScreen(false);
    setShowClosedScreen(true);
  };

  // Get button label with countdown - BULLETPROOF against Infinity/NaN
  const getNextButtonLabel = () => {
    const block = course?.blocks?.[currentBlockIndex];
    const isLast = currentBlockIndex === course.blocks.length - 1;
    const baseLabel = isLast ? t('player_nav_finish') : t('player_nav_next');
    
    // For video/audio blocks: show countdown ONLY if must_finish is enabled (or audio)
    if (block && (block.type === 'video' || block.type === 'audio')) {
      const mustFinishVideo = block.type === 'audio' || isMustFinishEnabled(block.props?.must_finish);
      if (mustFinishVideo && 
          !hasWatchedToEnd && 
          isPlaying && 
          remainingSeconds !== null && 
          Number.isFinite(remainingSeconds) && 
          remainingSeconds > 0) {
        return `${baseLabel} (${remainingSeconds}s)`;
      }
      return baseLabel;
    }
    
    // For time-gated blocks: show countdown
    if (timeRemaining > 0) {
      return `${baseLabel} (${timeRemaining}s)`;
    }
    
    return baseLabel;
  };

  // Render rich text content (supports HTML from editor)
  const renderRichText = (content) => {
    if (!content) return null;
    
    // Check if content looks like HTML
    if (content.includes('<') && content.includes('>')) {
      const sanitized = DOMPurify.sanitize(content, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'a'],
        ALLOWED_ATTR: ['href', 'target', 'rel']
      });
      return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitized }} />;
    }
    
    // Plain text
    return <p className="text-lg whitespace-pre-wrap leading-relaxed">{content}</p>;
  };

  // Render the current block content
  const renderBlockContent = () => {
    const block = course.blocks[currentBlockIndex];
    if (!block) return null;

    switch (block.type) {
      case 'text':
        return (
          <div className="h-full flex flex-col p-6 overflow-y-auto" data-testid="text-block">
            {/* Optional attached image */}
            {block.props.attached_image && (
              <div className="mb-4 flex-shrink-0">
                <img 
                  src={block.props.attached_image} 
                  alt="" 
                  className="w-full rounded-lg object-cover max-h-48"
                />
              </div>
            )}
            
            <div className="flex-1 flex flex-col justify-center">
              {renderRichText(block.props.content)}
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="h-full flex flex-col" data-testid="image-block">
            <div className="flex-1 flex items-center justify-center bg-black">
              <img 
                src={block.props.url} 
                alt={block.props.alt} 
                className="max-w-full max-h-full object-contain"
              />
            </div>
            {block.props.caption && (
              <p className="p-4 text-gray-600 text-center bg-white">{block.props.caption}</p>
            )}
          </div>
        );

      case 'video':
        // NO AUTOPLAY - NO media URLs loaded until user tap
        // CRITICAL: Do NOT add poster image before tap - iOS Safari will autoplay it
        
        return (
          <div 
            className="h-full flex flex-col relative bg-gradient-to-b from-slate-800 to-slate-900"
            data-testid="video-block"
          >
            {/* Before user taps: NO MEDIA URLS - pure CSS placeholder */}
            {!hasUserPressedPlay ? (
              <button
                type="button"
                className="flex-1 flex flex-col items-center justify-center cursor-pointer"
                onClick={handlePlayClick}
                data-testid="video-play-overlay"
              >
                {/* Pure CSS play button - NO poster image, NO video, NO media URLs */}
                <div className="w-24 h-24 rounded-full bg-white/90 flex items-center justify-center shadow-xl mb-4">
                  <Play className="h-12 w-12 text-slate-800 ml-1" />
                </div>
                <span className="text-white/80 text-sm">Tik om video af te spelen</span>
              </button>
            ) : (
              <>
                {/* After user taps: NOW load and render the video element */}
                <video
                  ref={videoRef}
                  src={block.props.mp4_url}
                  poster={block.props.poster}
                  className="w-full h-full object-contain"
                  playsInline
                  controls
                  controlsList="nofullscreen noremoteplayback nodownload"
                  disablePictureInPicture
                  preload="auto"
                  autoPlay
                  data-testid="video-player"
                  onLoadedMetadata={handleMediaLoadedMetadata}
                  onPlay={handleMediaPlay}
                  onPlaying={handleMediaPlaying}
                  onPause={handleMediaPause}
                  onEnded={handleMediaEnded}
                  onTimeUpdate={handleMediaTimeUpdate}
                  onError={handleMediaError}
                />
                
                {/* Show overlay on pause (before end) */}
                {!isPlaying && !hasWatchedToEnd && (
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/40 z-20"
                    onClick={() => {
                      // Safe play with error handling for iOS Safari
                      if (videoRef.current) {
                        videoRef.current.play().catch((err) => {
                          console.log('[Player] Resume play() rejected:', err.name, err.message);
                        });
                      }
                    }}
                    data-testid="video-play-overlay-resume"
                  >
                    <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                      <Play className="h-10 w-10 text-gray-800 ml-1" />
                    </div>
                  </button>
                )}
                
                {/* Helper text for must_finish videos */}
                {isMustFinishEnabled(block.props?.must_finish) && (
                  <>
                    {/* White instruction banner - show only first 2 seconds of playback */}
                    {showInstructionHint && !hasWatchedToEnd && (
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10 pointer-events-none transition-opacity duration-300">
                        <div className="px-4 py-2 rounded-full text-sm font-medium shadow-lg bg-white/90 text-slate-700">
                          {t('video_watch_hint')}
                        </div>
                      </div>
                    )}
                    
                    {/* Green completion banner - stays visible after video ends */}
                    {hasWatchedToEnd && (
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10 pointer-events-none">
                        <div className="px-4 py-2 rounded-full text-sm font-medium shadow-lg bg-green-500/90 text-white">
                          {t('video_watched_hint')}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="h-full flex flex-col justify-center p-6 bg-gradient-to-br from-purple-900 to-blue-900" data-testid="audio-block">
            <div className="text-center text-white mb-8">
              {block.props.title && (
                <h3 className="text-2xl font-bold mb-2">{block.props.title}</h3>
              )}
              {block.props.description && (
                <p className="opacity-80">{block.props.description}</p>
              )}
            </div>
            <audio 
              ref={audioRef}
              src={block.props.url} 
              controls 
              preload="metadata"
              className="w-full"
              data-testid="audio-player"
              onLoadedMetadata={handleMediaLoadedMetadata}
              onPlay={handleMediaPlay}
              onPlaying={handleMediaPlaying}
              onPause={handleMediaPause}
              onEnded={handleMediaEnded}
              onTimeUpdate={handleMediaTimeUpdate}
              onError={handleMediaError}
            />
          </div>
        );

      case 'quiz': {
        const hasCorrectAnswer = block.props.choices.some(c => c.correct);
        const isMysteryShop = course?.experience_type === 'mystery_shop';
        const allowMultipleAnswers = block.props.allow_multiple || false;
        
        // Check if this quiz is locked (already submitted)
        const lockedState = lockedQuizzes[block.id];
        const isQuizLocked = lockedState?.isLocked || false;
        
        // For locked quizzes: use the stored answers as the source of truth
        // For unlocked quizzes: use the current quizAnswers state
        const displayAnswers = isQuizLocked ? (lockedState?.answers || {}) : quizAnswers;
        
        // Determine if user answered correctly
        // For locked quizzes: use stored value. For unlocked: calculate from current state
        const selectedChoiceIds = Object.keys(displayAnswers).filter(id => displayAnswers[id]);
        const correctChoiceIds = block.props.choices.filter(c => c.correct).map(c => c.id);
        
        // Use stored correctness for locked quizzes, calculate for unlocked
        const userAnsweredCorrectly = isQuizLocked 
          ? lockedState?.isCorrect 
          : (hasCorrectAnswer && 
             selectedChoiceIds.length === correctChoiceIds.length &&
             selectedChoiceIds.every(id => correctChoiceIds.includes(id)));
        
        // Handle radio button selection (single answer only)
        // Completely disabled if quiz is locked
        const handleChoiceSelect = (choiceId, isChecked) => {
          // NEVER allow changes on locked quizzes
          if (isQuizLocked) return;
          
          if (allowMultipleAnswers) {
            // Checkbox: toggle the selected choice
            setQuizAnswers({ ...quizAnswers, [choiceId]: isChecked });
          } else {
            // Radio: clear all and select only this one
            if (isChecked) {
              const newAnswers = {};
              block.props.choices.forEach(c => {
                newAnswers[c.id] = c.id === choiceId;
              });
              setQuizAnswers(newAnswers);
            }
          }
        };
        
        return (
          <div className="h-full flex flex-col p-6 overflow-y-auto" data-testid="quiz-block">
            <h3 className="text-xl font-bold mb-2">{block.props.question}</h3>
            {/* Show locked indicator when quiz has been submitted */}
            {isQuizLocked && (
              <p className="text-sm text-gray-500 mb-4 flex items-center gap-1">
                <span>🔒</span> {t('quiz_locked') || 'Je antwoord is opgeslagen'}
              </p>
            )}
            <div className="space-y-3 mb-6 flex-1">
              {block.props.choices.map((choice, idx) => {
                // Use displayAnswers (locked or current) for selection state
                const isSelected = displayAnswers[choice.id];
                let styling = '';
                
                // Styling logic: for locked quizzes, always show result styling
                if (!showQuizResult && !isQuizLocked) {
                  styling = isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300';
                } else if (isMysteryShop) {
                  // Mystery shop: neutral styling, no correct/incorrect colors
                  styling = isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200';
                } else {
                  if (hasCorrectAnswer) {
                    if (choice.correct) {
                      styling = 'border-green-500 bg-green-50';
                    } else if (isSelected) {
                      styling = 'border-red-500 bg-red-50';
                    } else {
                      styling = 'border-gray-200';
                    }
                  } else {
                    if (isSelected) {
                      styling = 'border-purple-500 bg-purple-50';
                    } else {
                      styling = 'border-gray-200';
                    }
                  }
                }
                
                return (
                  <label
                    key={choice.id}
                    className={`flex items-center p-4 border-2 rounded ${isQuizLocked ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'} transition bg-white ${styling}`}
                    data-testid={`quiz-choice-${idx}`}
                  >
                    <input
                      type={allowMultipleAnswers ? "checkbox" : "radio"}
                      name={`quiz-${block.id}`}
                      checked={isSelected || false}
                      onChange={(e) => handleChoiceSelect(choice.id, e.target.checked)}
                      disabled={showQuizResult || isQuizLocked}
                      className="mr-3 h-4 w-4"
                    />
                    <span className="flex-1">{choice.text}</span>
                    {/* Show icons for locked quizzes OR when showQuizResult is true - skip for mystery_shop */}
                    {!isMysteryShop && (showQuizResult || isQuizLocked) && hasCorrectAnswer && choice.correct && (
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    )}
                    {!isMysteryShop && (showQuizResult || isQuizLocked) && hasCorrectAnswer && !choice.correct && isSelected && (
                      <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    )}
                    {(showQuizResult || isQuizLocked) && !hasCorrectAnswer && isSelected && (
                      <span className="text-sm text-purple-600 font-medium flex-shrink-0">{t('quiz_your_answer')}</span>
                    )}
                  </label>
                );
              })}
            </div>
            
            {/* Neutral question feedback - skip for mystery_shop */}
            {!isMysteryShop && (showQuizResult || isQuizLocked) && !hasCorrectAnswer && (
              <div className="bg-purple-50 border border-purple-200 rounded p-4 mb-4">
                <p className="text-sm text-purple-800">
                  {tm('quiz_reflection', course?.experience_type)}
                </p>
              </div>
            )}
            
            {/* Correct answer feedback - skip for mystery_shop */}
            {!isMysteryShop && (showQuizResult || isQuizLocked) && hasCorrectAnswer && userAnsweredCorrectly && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                  <CheckCircle className="h-5 w-5" />
                  {t('quiz_correct')}
                </div>
                {block.props.correct_explanation && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3">
                    <p className="text-sm text-emerald-800">{block.props.correct_explanation}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Incorrect answer feedback - skip for mystery_shop */}
            {!isMysteryShop && (showQuizResult || isQuizLocked) && hasCorrectAnswer && !userAnsweredCorrectly && (
              <div className="mb-4">
                <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
                  <XCircle className="h-5 w-5" />
                  {t('quiz_incorrect')}
                </div>
                {block.props.incorrect_explanation && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
                    <p className="text-sm text-amber-800">{block.props.incorrect_explanation}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Legacy explanation - skip for mystery_shop */}
            {!isMysteryShop && (showQuizResult || isQuizLocked) && block.props.explanation && !block.props.correct_explanation && !block.props.incorrect_explanation && (
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                <p className="text-sm">{block.props.explanation}</p>
              </div>
            )}

            {/* Optional comment field - all experience types, only when allow_comment */}
            {(showQuizResult || isQuizLocked) && block.props.allow_comment && (
              <div className="mb-4" data-testid={`quiz-comment-section-${block.id}`}>
                {!lockedQuizzes[block.id]?.commentSubmitted ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {block.props.comment_label || t('quiz_comment_label')}
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder={t('quiz_comment_placeholder')}
                      value={quizComments[block.id] || ''}
                      onChange={(e) => setQuizComments(prev => ({ ...prev, [block.id]: e.target.value }))}
                      data-testid={`quiz-comment-input-${block.id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        trackEvent('quiz_comment', {
                          block_id: block.id,
                          question: block.props?.question || '',
                          comment: quizComments[block.id] || ''
                        });
                        setLockedQuizzes(prev => ({
                          ...prev,
                          [block.id]: { ...prev[block.id], commentSubmitted: true, comment: quizComments[block.id] || '' }
                        }));
                      }}
                      data-testid={`quiz-comment-submit-${block.id}`}
                    >
                      {t('quiz_comment_submit')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic" data-testid={`quiz-comment-saved-${block.id}`}>
                    {lockedQuizzes[block.id]?.comment
                      ? `${block.props.comment_label || t('quiz_comment_label')}: ${lockedQuizzes[block.id].comment}`
                      : t('quiz_comment_skipped')
                    }
                  </div>
                )}
              </div>
            )}
            
            {/* Hide submit button for locked quizzes */}
            {!showQuizResult && !isQuizLocked && (
              <Button onClick={handleQuizSubmit} className="w-full" data-testid="quiz-submit-button">
                {t('quiz_confirm')}
              </Button>
            )}
          </div>
        );
      }

      case 'cta':
        return (
          <div className="h-full flex flex-col justify-center items-center p-6 bg-gradient-to-br from-blue-600 to-purple-600 text-white" data-testid="cta-block">
            <p className="text-xl mb-8 text-center">{block.props.text}</p>
            {block.props.button_url && (
              <Button 
                asChild 
                variant="secondary"
                size="lg"
                className="mb-4"
                data-testid="cta-action-button"
              >
                <a href={block.props.button_url} target="_blank" rel="noopener noreferrer">
                  {block.props.button_text}
                </a>
              </Button>
            )}
          </div>
        );

      default:
        return <div className="h-full flex items-center justify-center text-gray-500">{t('not_found')}</div>;
    }
  };

  // Loading state
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  // Course unavailable
  if (unavailable) {
    return <UnavailableScreen message={unavailableMessage} />;
  }

  // Course not found
  if (!course) {
    return <div className="min-h-screen flex items-center justify-center">{t('not_found')}</div>;
  }

  // Final closed screen
  if (showClosedScreen) {
    return <ClosedScreen />;
  }

  // Show StartScreen
  if (showStartScreen) {
    return (
      <StartScreen 
        onStart={handleStartScreenSubmit}
        courseName={course.title}
        clientStores={course.client?.stores || []}
        experienceType={course.experience_type}
      />
    );
  }

  // Show CompletionScreen
  if (showCompletionScreen) {
    return (
      <CompletionScreen 
        courseId={courseId}
        courseName={course.title}
        clientId={course.client_id}
        userData={userData}
        visitorId={visitorId}
        onFinish={handleFinish}
        experienceType={course.experience_type}
      />
    );
  }

  // Pre-start intro screen - WHITE background, fixed layout, NO scroll
  if (!started) {
    return (
      <div className="player-root bg-white" data-testid="player">
        {/* Content area - centered, no scroll */}
        <div className="player-content">
          <div className="w-full max-w-sm text-center px-4">
            <h1 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
              {course.title}
            </h1>
            {course.description && (
              <div 
                className="text-gray-700 mb-4 prose prose-sm max-w-none text-left"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.description) }}
              />
            )}
            <p className="text-xs text-gray-500">
              {course.blocks.length} {t('dashboard_blocks')}
            </p>
          </div>
        </div>
        
        {/* Fixed bottom button with safe area */}
        <div className="player-nav bg-white border-t border-gray-100">
          <div className="max-w-sm mx-auto">
            <Button size="lg" onClick={handleStart} className="w-full" data-testid="start-course-button">
              {tm('player_start_button', course?.experience_type)}
            </Button>
          </div>
        </div>

        {isLandscape && <PortraitOnlyOverlay />}
      </div>
    );
  }

  // Main player - fixed layout, NO scroll
  const progress = ((currentBlockIndex + 1) / Math.max(course.blocks.length, 1)) * 100;
  const nextEnabled = canGoToNext();

  return (
    <div className="player-root bg-gray-900" data-testid="player">
      {/* Top Chrome: Progress */}
      <div className="player-header bg-gray-800 border-b border-gray-700">
        <Progress value={progress} className="h-1 rounded-none" data-testid="progress-bar" />
        <div className="px-4 py-2 flex items-center justify-between text-sm text-gray-400">
          <span className="truncate mr-2">{course.title}</span>
          <span className="flex-shrink-0">{currentBlockIndex + 1} / {course.blocks.length}</span>
        </div>
      </div>

      {/* Main Content: ReelContainer - centered, no scroll */}
      <div className="player-content">
        <ReelContainer>
          {renderBlockContent()}
        </ReelContainer>
      </div>

      {/* Bottom Chrome: Navigation - fixed with safe area */}
      <div className="player-nav bg-gray-800 border-t border-gray-700">
        <div className="max-w-md mx-auto flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentBlockIndex === 0}
            className="flex-1 border-slate-400 bg-slate-800/80 text-white hover:bg-slate-700 hover:text-white disabled:opacity-50"
            data-testid="prev-button"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('player_nav_previous')}
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!nextEnabled}
            className="flex-1"
            data-testid="next-button"
          >
            {getNextButtonLabel()}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Portrait-only overlay — mobile touch devices only, landscape only */}
      {isLandscape && <PortraitOnlyOverlay />}
    </div>
  );
}

/**
 * PortraitOnlyOverlay
 * Non-dismissible full-viewport overlay shown when a mobile/touch device
 * enters landscape. Blocks all interaction. Auto-unmounts when the parent
 * flips `isLandscape` back to false (i.e. user rotates back to portrait).
 * Bilingual (NL primary + EN below).
 */
function PortraitOnlyOverlay() {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black text-white"
      style={{ touchAction: 'none' }}
      role="dialog"
      aria-modal="true"
      data-testid="portrait-only-overlay"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Rotating phone icon */}
      <svg
        width="72"
        height="72"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mb-6 text-white/90"
        aria-hidden="true"
      >
        <rect x="7" y="2" width="10" height="20" rx="2" ry="2" />
        <line x1="11" y1="18" x2="13" y2="18" />
      </svg>
      <p className="text-base font-semibold mb-1 px-6 text-center">
        Draai je telefoon rechtop om verder te gaan
      </p>
      <p className="text-sm text-white/70 px-6 text-center">
        Rotate your phone to portrait to continue
      </p>
    </div>
  );
}
